import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Component imports
import ProjectCard3D from './components/ProjectCard3D';
import CategoryFilter from './components/CategoryFilter';
import ProjectModal from './components/ProjectModal';
import GalleryStats from './components/GalleryStats';
import GridView from './components/GridView';
import TourGuide from './components/TourGuide';

const InteractivePortfolioGallery = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [viewMode, setViewMode] = useState('3d');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTourActive, setIsTourActive] = useState(false);
  const [focusedProject, setFocusedProject] = useState(null);

  // Mock project data
  const projects = [
    {
      id: 1,
      title: "E-Commerce Platform Revolution",
      category: "Web Development",
      client: "TechCorp Solutions",
      year: "2024",
      duration: "4 months",
      rating: "4.9",
      role: "Full Stack Developer",
      impact: "+150% conversion rate",
      description: "A cutting-edge e-commerce platform built with React and Node.js, featuring real-time inventory management and AI-powered recommendations.",
      fullDescription: `This comprehensive e-commerce platform represents a complete digital transformation for TechCorp Solutions. Built from the ground up using modern web technologies, the platform features a responsive React frontend with server-side rendering for optimal performance and SEO.\n\nThe backend architecture utilizes Node.js with Express, implementing microservices for scalability. Real-time features include live inventory updates, instant messaging with customer support, and dynamic pricing based on market conditions.\n\nKey innovations include an AI-powered recommendation engine that increased average order value by 35%, and a progressive web app implementation that improved mobile conversion rates by 60%.`,
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
      technologies: ["React", "Node.js", "MongoDB", "Redis", "AWS", "Stripe"],
      liveUrl: "https://example.com",
      githubUrl: "https://github.com/example",
      gallery: [
        "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop"
      ],
      processSteps: [
        { title: "Discovery & Research", description: "Conducted comprehensive market analysis and user research to identify key pain points and opportunities." },
        { title: "Architecture Design", description: "Designed scalable microservices architecture with focus on performance and maintainability." },
        { title: "Development & Testing", description: "Implemented features using agile methodology with continuous integration and automated testing." },
        { title: "Deployment & Optimization", description: "Deployed to AWS with performance monitoring and continuous optimization based on user feedback." }
      ],
      architecture: "Microservices architecture with React frontend, Node.js backend, MongoDB database, and Redis caching layer.",
      challenges: "Handling high-traffic loads during flash sales, implementing real-time inventory synchronization across multiple warehouses.",
      metrics: [
        { label: "Conversion Rate", value: "+150%" },
        { label: "Page Load Time", value: "1.2s" },
        { label: "User Satisfaction", value: "4.9/5" }
      ]
    },
    {
      id: 2,
      title: "Interactive Data Visualization",
      category: "Creative Coding",
      client: "DataViz Inc",
      year: "2024",
      duration: "3 months",
      rating: "4.8",
      role: "Creative Developer",
      impact: "+200% user engagement",
      description: "An immersive 3D data visualization platform using Three.js and D3.js, transforming complex datasets into explorable virtual environments.",
      fullDescription: `This groundbreaking data visualization platform transforms traditional charts and graphs into immersive 3D experiences. Users can literally walk through their data, discovering patterns and insights that would be impossible to see in conventional 2D representations.\n\nThe platform combines the power of Three.js for 3D rendering with D3.js for data processing, creating smooth transitions between different visualization modes. Real-time data streaming ensures that visualizations stay current with live data sources.\n\nInteractive features include gesture controls for VR headsets, voice commands for data queries, and collaborative viewing sessions where multiple users can explore data together in real-time.`,
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
      technologies: ["Three.js", "D3.js", "WebGL", "WebRTC", "Python", "TensorFlow"],
      liveUrl: "https://example.com",
      githubUrl: "https://github.com/example",
      gallery: [
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&h=600&fit=crop"
      ],
      processSteps: [
        { title: "Data Analysis", description: "Analyzed complex datasets to identify optimal visualization strategies and user interaction patterns." },
        { title: "3D Prototyping", description: "Created interactive prototypes to test different approaches to 3D data representation." },
        { title: "Performance Optimization", description: "Optimized rendering pipeline for smooth 60fps performance with large datasets." },
        { title: "User Testing", description: "Conducted extensive user testing to refine interaction models and improve accessibility." }
      ],
      architecture: "WebGL-based 3D engine with real-time data streaming, WebRTC for collaboration, and machine learning for pattern recognition.",
      challenges: "Rendering millions of data points in real-time while maintaining smooth interactions and cross-platform compatibility.",
      metrics: [
        { label: "Data Points", value: "10M+" },
        { label: "Frame Rate", value: "60fps" },
        { label: "Load Time", value: "2.1s" }
      ]
    },
    {
      id: 3,
      title: "Brand Identity Campaign",
      category: "Brand Campaigns",
      client: "Creative Studios",
      year: "2023",
      duration: "6 months",
      rating: "4.7",
      role: "Creative Director",
      impact: "+85% brand recognition",
      description: "A comprehensive brand identity campaign featuring interactive web experiences, motion graphics, and digital installations.",
      fullDescription: `This comprehensive brand identity campaign redefined Creative Studios' market presence through a multi-channel approach combining traditional design principles with cutting-edge digital experiences.\n\nThe campaign included a complete visual identity overhaul, interactive website redesign, motion graphics for social media, and physical digital installations for trade shows. Each touchpoint was carefully crafted to reinforce the brand's core values of innovation and creativity.\n\nThe digital installations featured motion-sensing displays that responded to viewer presence, creating personalized brand experiences. The website incorporated advanced animations and micro-interactions that brought the brand story to life.`,
      image: "https://images.unsplash.com/photo-1558655146-d09347e92766?w=800&h=600&fit=crop",
      technologies: ["After Effects", "Cinema 4D", "React", "GSAP", "Arduino", "TouchDesigner"],
      liveUrl: "https://example.com",
      githubUrl: "https://github.com/example",
      gallery: [
        "https://images.unsplash.com/photo-1558655146-d09347e92766?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=800&h=600&fit=crop"
      ],
      processSteps: [
        { title: "Brand Strategy", description: "Developed comprehensive brand strategy based on market research and competitive analysis." },
        { title: "Visual Identity", description: "Created cohesive visual identity system including logo, typography, and color palette." },
        { title: "Digital Experiences", description: "Designed and developed interactive digital touchpoints across web and mobile platforms." },
        { title: "Campaign Launch", description: "Executed multi-channel launch campaign with performance tracking and optimization." }
      ],
      architecture: "Multi-platform campaign architecture with web applications, mobile experiences, and physical installations.",
      challenges: "Maintaining brand consistency across diverse platforms while creating unique, memorable experiences for each touchpoint.",
      metrics: [
        { label: "Brand Recognition", value: "+85%" },
        { label: "Engagement Rate", value: "+120%" },
        { label: "Social Reach", value: "2.5M" }
      ]
    },
    {
      id: 4,
      title: "Mobile Banking App",
      category: "Mobile Apps",
      client: "FinTech Innovations",
      year: "2024",
      duration: "5 months",
      rating: "4.9",
      role: "Mobile Developer",
      impact: "+300% user adoption",
      description: "A secure and intuitive mobile banking application with biometric authentication and real-time transaction monitoring.",
      fullDescription: `This next-generation mobile banking application sets new standards for security, usability, and performance in the fintech industry. Built with React Native for cross-platform compatibility, the app features advanced biometric authentication, real-time fraud detection, and AI-powered financial insights.\n\nSecurity was paramount in the development process, implementing end-to-end encryption, secure key storage, and multi-factor authentication. The user experience focuses on simplicity without compromising functionality, featuring intuitive navigation and personalized dashboards.\n\nInnovative features include voice-activated transactions, augmented reality for ATM location, and machine learning algorithms that provide personalized financial advice and spending insights.`,
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop",
      technologies: ["React Native", "Node.js", "PostgreSQL", "AWS", "Biometric APIs", "ML Kit"],
      liveUrl: "https://example.com",
      githubUrl: "https://github.com/example",
      gallery: [
        "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop"
      ],
      processSteps: [
        { title: "Security Assessment", description: "Conducted comprehensive security audit and implemented industry-leading security measures." },
        { title: "UX Research", description: "Performed extensive user research to understand banking behavior and pain points." },
        { title: "Development", description: "Built cross-platform application with focus on performance and security." },
        { title: "Testing & Launch", description: "Rigorous testing including penetration testing and user acceptance testing." }
      ],
      architecture: "React Native frontend with Node.js backend, PostgreSQL database, and AWS cloud infrastructure with advanced security layers.",
      challenges: "Balancing maximum security with optimal user experience, ensuring compliance with banking regulations across multiple jurisdictions.",
      metrics: [
        { label: "User Adoption", value: "+300%" },
        { label: "Security Score", value: "99.9%" },
        { label: "App Rating", value: "4.9/5" }
      ]
    },
    {
      id: 5,
      title: "Design System Framework",
      category: "UI/UX Design",
      client: "Enterprise Corp",
      year: "2023",
      duration: "8 months",
      rating: "4.8",
      role: "Design System Lead",
      impact: "+400% design efficiency",
      description: "A comprehensive design system framework with component library, design tokens, and automated documentation generation.",
      fullDescription: `This enterprise-grade design system framework revolutionizes how large organizations approach design consistency and development efficiency. The system includes a comprehensive component library, design tokens for consistent styling, and automated documentation that stays synchronized with code changes.\n\nThe framework supports multiple platforms including web, mobile, and desktop applications, ensuring consistent user experiences across all touchpoints. Advanced features include automated accessibility testing, design token validation, and integration with popular design tools.\n\nThe system has been adopted by over 50 development teams, reducing design-to-development time by 60% and ensuring consistent brand experiences across all digital products.`,
      image: "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=800&h=600&fit=crop",
      technologies: ["Storybook", "Figma", "React", "TypeScript", "Sass", "Jest"],
      liveUrl: "https://example.com",
      githubUrl: "https://github.com/example",
      gallery: [
        "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop"
      ],
      processSteps: [
        { title: "Audit & Analysis", description: "Comprehensive audit of existing design patterns and identification of inconsistencies." },
        { title: "System Architecture", description: "Designed scalable system architecture supporting multiple platforms and frameworks." },
        { title: "Component Development", description: "Built comprehensive component library with extensive documentation and examples." },
        { title: "Adoption & Training", description: "Implemented adoption strategy with training programs and ongoing support." }
      ],
      architecture: "Multi-platform design system with React components, design tokens, automated testing, and continuous integration.",
      challenges: "Creating a system flexible enough for diverse use cases while maintaining consistency, ensuring adoption across multiple teams.",
      metrics: [
        { label: "Design Efficiency", value: "+400%" },
        { label: "Team Adoption", value: "50+" },
        { label: "Consistency Score", value: "95%" }
      ]
    },
    {
      id: 6,
      title: "AI-Powered Analytics Dashboard",
      category: "Web Development",
      client: "Analytics Pro",
      year: "2024",
      duration: "4 months",
      rating: "4.9",
      role: "Frontend Architect",
      impact: "+250% data insights",
      description: "An intelligent analytics dashboard powered by machine learning algorithms, providing real-time insights and predictive analytics.",
      fullDescription: `This cutting-edge analytics dashboard leverages artificial intelligence to transform raw data into actionable insights. Built with modern web technologies, the platform processes millions of data points in real-time, providing users with predictive analytics and automated recommendations.\n\nThe dashboard features advanced data visualization techniques, including interactive 3D charts, heat maps, and timeline visualizations. Machine learning algorithms continuously analyze user behavior to provide personalized insights and recommendations.\n\nKey innovations include natural language query processing, allowing users to ask questions in plain English and receive instant visual answers, and automated anomaly detection that alerts users to unusual patterns in their data.`,
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",
      technologies: ["React", "D3.js", "Python", "TensorFlow", "WebSocket", "PostgreSQL"],
      liveUrl: "https://example.com",
      githubUrl: "https://github.com/example",
      gallery: [
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&h=600&fit=crop"
      ],
      processSteps: [
        { title: "Data Architecture", description: "Designed scalable data pipeline capable of processing millions of records in real-time." },
        { title: "ML Model Development", description: "Developed and trained machine learning models for predictive analytics and anomaly detection." },
        { title: "Frontend Development", description: "Built responsive dashboard with advanced data visualization and real-time updates." },
        { title: "Performance Optimization", description: "Optimized for high-performance data processing and smooth user interactions." }
      ],
      architecture: "React frontend with real-time WebSocket connections, Python backend with TensorFlow ML models, and PostgreSQL data warehouse.",
      challenges: "Processing and visualizing massive datasets in real-time while maintaining responsive user interface and accurate predictions.",
      metrics: [
        { label: "Data Processing", value: "10M/sec" },
        { label: "Prediction Accuracy", value: "94%" },
        { label: "Response Time", value: "<100ms" }
      ]
    }
  ];

  const categories = ['All', ...new Set(projects.map(p => p.category))];

  const filteredProjects = activeCategory === 'All' 
    ? projects 
    : projects?.filter(p => p?.category === activeCategory);

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleTourComplete = () => {
    setIsTourActive(false);
    setFocusedProject(null);
  };

  const handleProjectFocus = (project) => {
    setFocusedProject(project);
  };

  return (
    <>
      <Helmet>
        <title>Interactive Portfolio Gallery - 3D Portfolio Showcase</title>
        <meta name="description" content="Explore projects in immersive 3D environments. Interactive portfolio gallery featuring web development, creative coding, and brand campaigns." />
        <meta name="keywords" content="3D portfolio, interactive gallery, web development, creative coding, brand campaigns, UI/UX design" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center space-y-6 mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-accent to-cosmic-energy rounded-xl flex items-center justify-center cosmic-glow">
                  <Icon name="Box" size={24} className="text-background" />
                </div>
                <h1 className="font-headline font-bold text-4xl lg:text-6xl text-foreground">
                  Interactive Portfolio
                  <span className="block text-accent">Gallery</span>
                </h1>
              </div>
              
              <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Experience projects in immersive 3D environments where each work sample becomes an explorable installation. 
                Navigate through spatial categories and discover the stories behind every creation.
              </p>

              <div className="flex items-center justify-center space-x-4">
                <Button
                  variant="default"
                  onClick={() => setIsTourActive(true)}
                  iconName="Play"
                  iconPosition="left"
                  className="bg-accent text-background hover:bg-accent/90 cosmic-glow"
                >
                  Start Guided Tour
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setViewMode(viewMode === '3d' ? 'grid' : '3d')}
                  iconName={viewMode === '3d' ? 'Grid3x3' : 'Box'}
                  iconPosition="left"
                  className="border-accent/30 text-accent hover:bg-accent/10"
                >
                  Switch to {viewMode === '3d' ? 'Grid' : '3D'} View
                </Button>
              </div>
            </motion.div>

            {/* Gallery Stats */}
            <GalleryStats projects={projects} activeCategory={activeCategory} />

            {/* Category Filter */}
            <CategoryFilter
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />

            {/* Projects Gallery */}
            <motion.div
              key={`${activeCategory}-${viewMode}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {viewMode === '3d' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredProjects?.map((project, index) => (
                    <ProjectCard3D
                      key={project?.id}
                      project={project}
                      index={index}
                      onProjectSelect={handleProjectSelect}
                      isActive={focusedProject?.id === project?.id}
                    />
                  ))}
                </div>
              ) : (
                <GridView
                  projects={filteredProjects}
                  onProjectSelect={handleProjectSelect}
                />
              )}
            </motion.div>

            {/* Empty State */}
            {filteredProjects?.length === 0 && (
              <motion.div
                className="text-center py-16"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="w-24 h-24 bg-surface/50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Search" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="font-headline font-bold text-xl text-foreground mb-2">
                  No Projects Found
                </h3>
                <p className="font-body text-muted-foreground mb-6">
                  No projects match the selected category. Try selecting a different category.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setActiveCategory('All')}
                  iconName="RotateCcw"
                  iconPosition="left"
                  className="border-accent/30 text-accent hover:bg-accent/10"
                >
                  Show All Projects
                </Button>
              </motion.div>
            )}
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 px-6 lg:px-8 border-t border-border/20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground">
                Ready to Create Something
                <span className="block text-accent">Amazing Together?</span>
              </h2>
              
              <p className="font-body text-xl text-muted-foreground max-w-2xl mx-auto">
                Let's discuss your next project and explore how we can bring your vision to life 
                through innovative design and cutting-edge technology.
              </p>

              <div className="flex items-center justify-center space-x-4">
                <Button
                  variant="default"
                  onClick={() => window.location.href = '/contact-nexus'}
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="bg-accent text-background hover:bg-accent/90 cosmic-glow"
                >
                  Start a Conversation
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.location.href = '/process-laboratory'}
                  iconName="Workflow"
                  iconPosition="left"
                  className="border-accent/30 text-accent hover:bg-accent/10"
                >
                  View Process
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Project Modal */}
        <ProjectModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />

        {/* Tour Guide */}
        <TourGuide
          projects={projects}
          isActive={isTourActive}
          onTourComplete={handleTourComplete}
          onProjectFocus={handleProjectFocus}
        />

        {/* Cosmic Background Effects */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-accent/5 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cosmic-energy/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-128 h-128 bg-cosmic-cta/3 rounded-full blur-3xl animate-pulse-glow"></div>
        </div>
      </div>
    </>
  );
};

export default InteractivePortfolioGallery;